/**
 * System Prompt Builder - XML-structured generation
 * Phase 2: Modular XML template builder for LLM prompts
 *
 * Category-aware: uses CallCategory (10 values) for rubrics,
 * falls back to BaseCategory (4 values) for examples and quality framework.
 *
 * Channel is the second axis and is passed through everywhere it changes what
 * can be measured: rubrics, weights, the guideline set and the output-format
 * example. It is NOT a substitute for the category — see `exampleCategory`
 * below for the one place where it deliberately replaces it, and why.
 */

import { resolveChannelToggles } from '@aica/core';
import type { CallCategory, Channel } from '../call-categories';
import { baseToCallCategory } from '../call-categories';
import { ALL_TOGGLES_ON } from '../profiles';
import {
  XML_COMMON,
  buildAttentionFlagsSection,
  getQualityScoreFramework,
} from './common-sections';
import { SALES_CALL_EXAMPLES } from './sales-call-examples.xml';
import { SUPPORT_CALL_EXAMPLES } from './support-call-examples.xml';
import { SALES_CHAT_EXAMPLES } from './sales-chat-examples.xml';
import { COMPLAINT_EXAMPLES } from './complaint-examples.xml';
import { getOutputFormatSchema } from './output-format';
import { buildAnalysisGuidelines } from './analysis-guidelines';

type BaseCategory = 'sales-call' | 'support-call' | 'sales-chat' | 'complaint';

export interface SystemBuilderOptions {
  category: BaseCategory;
  /**
   * No `toggles` field, deliberately.
   *
   * The system prompt is toggle-INDEPENDENT by design: it always describes the
   * widest set this request could accept (`ALL_TOGGLES_ON`, narrowed only by
   * what the CHANNEL makes impossible), so it stays prompt-cache-stable across
   * projects, and the per-record enabled subset is named in the user prompt's
   * `<analysis_toggles>` instead. No profile setting reaches this string.
   *
   * The field used to exist and be required. Nothing read it: every caller
   * computed and threaded a `PromptToggles` value that this builder
   * destructured away, so a reader of `BOUND_PROFILE_SPECS.toggleDeltas`
   * (e.g. `scheduling: { objectionHandling: false }`) reasonably concluded
   * those deltas shaped the stored system prompt. They never did — only
   * `callCategory` and the base `category` do.
   */
  includeExamples: boolean;
  callCategory?: CallCategory;
  channel?: Channel;
  targetCrmFields?: Array<{
    fieldId: string;
    title: string;
    type: string;
    enumValues?: Array<{ id: string; value: string }>;
  }>;
}

/**
 * Build complete XML-structured system prompt
 */
export function buildXMLSystemPrompt(options: SystemBuilderOptions): string {
  const { category, includeExamples } = options;
  const channel: Channel =
    options.channel || (category === 'sales-chat' ? 'chat' : 'call');
  const callCat: CallCategory =
    options.callCategory || baseToCallCategory(category, channel);

  // Chat gets chat-shaped few-shots regardless of the bound profile's base
  // category — all 10 bound profiles have a sales-call/support-call/complaint
  // base (no bound profile is sales-chat), so without this a chat inherits
  // CALL examples while the channel toggle mask (resolveChannelToggles) has
  // already stripped the call-only criteria they demonstrate. For channel
  // 'call' this is `category` unchanged — the call path is untouched.
  //
  // Only the EXAMPLES, though. The same substitution used to drive the
  // evaluation weights too, and there it was wrong: it scored a support or
  // complaint chat by the sales weight sheet, where problem resolution and
  // de-escalation weigh zero, while the role, rubrics and guidelines beside it
  // stayed on the profile's real category. `getQualityScoreFramework` now
  // takes the channel as its own axis and keeps each category's priorities
  // (see its doc comment).
  //
  // The examples genuinely are channel calibration rather than category
  // teaching: SALES_CHAT_EXAMPLES scores message brevity, response time, link
  // sharing and emoji use — the things that differ because it is a chat. The
  // call sets are the opposite (SUPPORT_CALL_EXAMPLES calibrates "first call
  // resolution" and "call us back"), which is why they must not be shown for a
  // conversation that was never a call.
  const exampleCategory: BaseCategory = channel === 'chat' ? 'sales-chat' : category;

  const sections: string[] = [];

  // 1. Role section
  sections.push(`<system>`);
  sections.push(`  <role>`);
  sections.push(getRoleDescription(callCat));
  sections.push(`  </role>`);

  // 2. Evaluation Framework
  sections.push(`  <evaluation_framework>`);
  sections.push(`    ${getQualityScoreFramework(category, channel)}`);
  sections.push(`  </evaluation_framework>`);

  // 3. Scoring Rubrics — ALL included for prompt cache stability
  // Active/disabled toggles communicated in user prompt, not system prompt
  sections.push(`  <scoring_rubrics>`);
  sections.push(`    ${XML_COMMON.universalScale}`);
  sections.push(getGreetingRubric(callCat, channel));
  sections.push(getNeedsQualificationRubric(callCat, channel));
  sections.push(getObjectionHandlingRubric());
  sections.push(getClosingRubric(callCat, channel));
  sections.push(`  </scoring_rubrics>`);

  // The widest toggle set this REQUEST could possibly accept.
  //
  // ALL_TOGGLES_ON keeps the system prompt cache-stable across projects (the
  // per-record enabled subset is named in the user prompt's <analysis_toggles>,
  // and that subset is exactly what builds the strict schema in the analyze
  // worker). But the widest set must still respect what the CHANNEL makes
  // impossible: `resolveChannelToggles` drops key_moments / filler_words /
  // interruptions / clarity from a chat (properties of speech), and the strict
  // schema is built from those same channel-resolved toggles.
  //
  // ONE value feeds both the guidelines and the output-format example: they
  // used to be masked separately (guidelines yes, example no), so a chat was
  // shown an example carrying fields the schema for that same request rejects.
  const channelToggles = resolveChannelToggles({ ...ALL_TOGGLES_ON }, channel);

  // 3.5. Analysis Guidelines — ALL included (toggle-gating moved to user prompt)
  const guidelinesSection = buildAnalysisGuidelines({
    toggles: channelToggles,
    category,
    callCategory: callCat,
    channel,
    targetCrmFields: options.targetCrmFields,
  });
  if (guidelinesSection) {
    sections.push(guidelinesSection);
  }

  // 4. Chain of Thought
  sections.push(`  ${XML_COMMON.chainOfThought}`);

  // 5. Examples (if enabled) - Phase 2.2
  if (includeExamples) {
    sections.push(getCategoryExamples(exampleCategory));
  }

  // 6. Output Format — every field this channel can accept (the per-record
  // enabled subset is named in the user prompt's <analysis_toggles>)
  sections.push(
    getOutputFormatSchema({
      toggles: channelToggles,
      category,
      callCategory: callCat,
    })
  );

  // 7. Attention Flags — always included
  sections.push(`  ${buildAttentionFlagsSection()}`);

  // 8. Security
  sections.push(`  ${XML_COMMON.security}`);

  sections.push(`</system>`);

  return sections.join('\n');
}

// ============================================================================
// Role Description (10 unique roles)
// ============================================================================

/**
 * Get role description for call category
 */
export function getRoleDescription(category: CallCategory): string {
  switch (category) {
    case 'purchase':
      return `You are a sales QA analyst with deep expertise in BANT, MEDDIC, SPIN selling methodologies, closing techniques, and conversion optimization. Your goal is to provide objective, actionable feedback that helps sales representatives improve deal closure rates and sales performance.`;

    case 'product_inquiry':
      return `You are a product consultation QA analyst with deep expertise in product knowledge delivery, needs discovery, and solution mapping. Your goal is to provide objective, actionable feedback that helps consultants accurately present products and effectively identify customer needs.`;

    case 'scheduling':
      return `You are a meeting coordination QA analyst with expertise in appointment scheduling, meeting preparation, and stakeholder engagement. Your goal is to provide objective, actionable feedback that helps coordinators efficiently schedule and prepare productive meetings.`;

    case 'support':
      return `You are a technical support QA expert with deep knowledge of troubleshooting methodologies, first-call resolution techniques, and customer service best practices. Your goal is to provide objective, actionable feedback that helps support agents diagnose issues faster and resolve them on first contact.`;

    case 'billing':
      return `You are a billing support QA expert with expertise in financial inquiry resolution, payment processing accuracy, and billing dispute handling. Your goal is to provide objective, actionable feedback that helps billing agents resolve financial questions accurately and efficiently.`;

    case 'onboarding':
      return `You are an onboarding QA expert with expertise in customer education, structured training delivery, and adoption acceleration. Your goal is to provide objective, actionable feedback that helps onboarding specialists guide new customers through setup effectively.`;

    case 'complaint':
      return `You are a complaint handling QA expert with deep knowledge of de-escalation techniques, risk assessment, customer retention strategies, and conflict resolution. Your goal is to provide objective, actionable feedback that helps agents resolve complaints effectively while minimizing legal and reputational risks.`;

    case 'cancellation':
      return `You are a retention QA expert with expertise in churn prevention, retention offer management, and graceful service termination. Your goal is to provide objective, actionable feedback that helps retention agents understand cancellation reasons and maximize save rates.`;

    case 'follow_up':
      return `You are a follow-up QA analyst with expertise in context continuity, relationship management, and deal progression. Your goal is to provide objective, actionable feedback that helps agents maintain conversation context and advance customer relationships toward resolution.`;

    case 'other':
      return `You are a general communication QA analyst with expertise in professional communication, active listening, and customer service fundamentals. Your goal is to provide objective, actionable feedback that helps agents communicate clearly and achieve productive conversation outcomes.`;
  }
}

// ============================================================================
// Greeting Rubric (channel-specific)
// ============================================================================

/**
 * Get greeting rubric — depends on channel (call vs chat), not category
 */
export function getGreetingRubric(category: CallCategory, channel: Channel): string {
  if (channel === 'chat') {
    return `
    <greeting_intro channel="chat">
      <note>Do NOT penalize for missing formal company introduction — chat greetings are naturally shorter.</note>
      <criteria>
        <criterion name="personalization" weight="1">Used customer's name or acknowledged their context</criterion>
        <criterion name="response_timeliness" weight="1">Quick initial response, no long delay before first message</criterion>
        <criterion name="tone_appropriate" weight="1">Professional yet friendly tone matching chat medium</criterion>
        <criterion name="context_awareness" weight="1">Referenced customer's question/issue from first message</criterion>
      </criteria>
      <calculation>(sum / 4) × 100</calculation>
    </greeting_intro>`;
  }

  // Call greeting (all categories)
  return `
    <greeting_intro channel="call">
      <criteria>
        <criterion name="personalization" weight="1">Used customer's name</criterion>
        <criterion name="company_identification" weight="1">Identified company/self</criterion>
        <criterion name="purpose_stated" weight="1">Stated call purpose</criterion>
        <criterion name="rapport_building" weight="1">Friendly tone established</criterion>
      </criteria>
      <direction_note>
        All four criteria apply to outbound calls and to calls of unknown direction.
        On an inbound call (the request marks the direction "inbound") the customer brings the
        reason for calling and often has not given a name yet: purpose_stated is met when the
        employee identifies and confirms the customer's request, and personalization applies
        only when the customer's name is known (CRM context, or the customer gave it) —
        otherwise it is not applicable and leaves the count.
      </direction_note>
      <calculation>(sum of applicable criteria / number of applicable criteria) × 100</calculation>
    </greeting_intro>`;
}

// ============================================================================
// Needs Qualification Rubric (10 category-specific frameworks × 2 channels)
// ============================================================================

/**
 * Shared across every category branch of `getNeedsQualificationRubricBody`.
 *
 * The rule used to exist in exactly two places: the `needs_qualification`
 * field guideline (analysis-guidelines.ts) and the `purchase` rubric's own
 * `<applicability>` clause. The other nine rubrics kept a hard
 * `(sum of scores / 8) × 100` right underneath — so nine categories out of ten
 * carried two conflicting rules for one score, and the arithmetic one is the
 * one printed next to the components the model is filling in. A component that
 * could not apply then costs 2 points of a fixed denominator, which is the
 * "50/100 plus advice to ask who approves the renewal" shape the 2026-08-15
 * audit found on consumer calls — reproduced in every non-purchase framework.
 *
 * Appended once, in the wrapper below, so every category gets it — adding an
 * 11th framework can never forget it.
 *
 * Parametrized by channel for the same reason `getClosingNotApplicableNote`
 * is: chat now routes through all 10 categories, and "this call" is false on a
 * conversation that was never a call.
 */
function getNeedsQualificationApplicabilityNote(channel: Channel): string {
  const subject = channel === 'chat' ? 'conversation' : 'call';
  return `
    <needs_qualification_applicability_note>
      A component that could not sensibly apply to THIS ${subject} is not a
      failure: mark it not applicable and DROP it from the denominator instead
      of scoring it 0 (two of four components applicable => sum / 4, not / 8).
      Never score a component 0 for going unasked when asking it would have
      made no sense, and never advise the agent to ask it.
      If NO component applies, qualification was not this ${subject}'s job at
      all: return score=null rather than 0 — 0 would read as "did it badly".
    </needs_qualification_applicability_note>`;
}

/**
 * Get needs qualification rubric — category + channel specific
 */
export function getNeedsQualificationRubric(
  category: CallCategory,
  channel: Channel
): string {
  return `${getNeedsQualificationRubricBody(category, channel)}${getNeedsQualificationApplicabilityNote(channel)}`;
}

function getNeedsQualificationRubricBody(
  category: CallCategory,
  channel: Channel
): string {
  const subject = channel === 'chat' ? 'conversation' : 'call';
  switch (category) {
    case 'purchase':
      return `
    <needs_qualification framework="BANT">
      <component name="budget" max_score="2">
        <score value="0">Not asked</score>
        <score value="1">Mentioned</score>
        <score value="2">Specific amount/range discussed</score>
      </component>
      <component name="authority" max_score="2">
        <score value="0">Unknown</score>
        <score value="1">Identified</score>
        <score value="2">Confirmed decision maker</score>
      </component>
      <component name="need" max_score="2">
        <score value="0">Assumed</score>
        <score value="1">Stated</score>
        <score value="2">Quantified with business impact</score>
      </component>
      <component name="timeline" max_score="2">
        <score value="0">None</score>
        <score value="1">Vague ('soon', 'next quarter')</score>
        <score value="2">Specific date/quarter</score>
      </component>
      <applicability>
        This rubric is built for B2B selling. With a consumer — a private person
        buying or renewing a service for themselves, at a fixed published price
        — "budget" and "authority" are not things the agent failed to do: the
        customer IS the decision maker and there is no budget to uncover. Treat
        them as not applicable on such a ${subject}.
      </applicability>
      <calculation>(sum of applicable scores / (2 × number of applicable components)) × 100; null when no component applies</calculation>
    </needs_qualification>`;

    case 'product_inquiry':
      return `
    <needs_qualification framework="Problem-Solution Fit">
      <component name="problem_identified" max_score="2">
        <score value="0">Not explored</score>
        <score value="1">General problem stated</score>
        <score value="2">Specific pain point with impact quantified</score>
      </component>
      <component name="current_solution" max_score="2">
        <score value="0">Not asked</score>
        <score value="1">Current tool/process mentioned</score>
        <score value="2">Limitations of current solution identified</score>
      </component>
      <component name="expectations" max_score="2">
        <score value="0">Not explored</score>
        <score value="1">General expectations stated</score>
        <score value="2">Specific success criteria defined</score>
      </component>
      <component name="budget_scope" max_score="2">
        <score value="0">Not discussed</score>
        <score value="1">Willingness to invest mentioned</score>
        <score value="2">Budget range or approval process clarified</score>
      </component>
      <calculation>(sum of applicable scores / (2 × number of applicable components)) × 100; null when no component applies</calculation>
    </needs_qualification>`;

    case 'scheduling':
      return `
    <needs_qualification framework="Meeting Prep">
      <component name="meeting_goal" max_score="2">
        <score value="0">Not clarified</score>
        <score value="1">General purpose stated</score>
        <score value="2">Specific agenda and expected outcomes defined</score>
      </component>
      <component name="participants" max_score="2">
        <score value="0">Not identified</score>
        <score value="1">Key participant named</score>
        <score value="2">All attendees confirmed with roles</score>
      </component>
      <component name="preparation" max_score="2">
        <score value="0">No preparation discussed</score>
        <score value="1">Basic materials mentioned</score>
        <score value="2">Materials/demos prepared and confirmed</score>
      </component>
      <component name="agenda_clarity" max_score="2">
        <score value="0">No agenda</score>
        <score value="1">Topics mentioned</score>
        <score value="2">Structured agenda with time allocation</score>
      </component>
      <calculation>(sum of applicable scores / (2 × number of applicable components)) × 100; null when no component applies</calculation>
    </needs_qualification>`;

    case 'support':
      return `
    <needs_qualification framework="Troubleshooting">
      <component name="symptom_identified" max_score="2">
        <score value="0">Not clarified</score>
        <score value="1">General issue described</score>
        <score value="2">Specific symptom with error details captured</score>
      </component>
      <component name="reproduction" max_score="2">
        <score value="0">Not attempted</score>
        <score value="1">Steps partially explored</score>
        <score value="2">Full reproduction steps confirmed</score>
      </component>
      <component name="environment" max_score="2">
        <score value="0">Not checked</score>
        <score value="1">Basic info (version) collected</score>
        <score value="2">Full environment details (OS, version, config) documented</score>
      </component>
      <component name="urgency" max_score="2">
        <score value="0">Not assessed</score>
        <score value="1">Impact acknowledged</score>
        <score value="2">Business impact quantified, priority set</score>
      </component>
      <calculation>(sum of applicable scores / (2 × number of applicable components)) × 100; null when no component applies</calculation>
    </needs_qualification>`;

    case 'billing':
      return `
    <needs_qualification framework="Issue ID">
      <component name="invoice_reference" max_score="2">
        <score value="0">Not collected</score>
        <score value="1">Account mentioned</score>
        <score value="2">Specific invoice/transaction number confirmed</score>
      </component>
      <component name="amount_details" max_score="2">
        <score value="0">Not discussed</score>
        <score value="1">Amount mentioned</score>
        <score value="2">Expected vs actual amount compared</score>
      </component>
      <component name="period" max_score="2">
        <score value="0">Not identified</score>
        <score value="1">General timeframe mentioned</score>
        <score value="2">Specific billing period/date confirmed</score>
      </component>
      <component name="discrepancy" max_score="2">
        <score value="0">Not clarified</score>
        <score value="1">General complaint stated</score>
        <score value="2">Specific discrepancy between expected and actual documented</score>
      </component>
      <calculation>(sum of applicable scores / (2 × number of applicable components)) × 100; null when no component applies</calculation>
    </needs_qualification>`;

    case 'onboarding':
      return `
    <needs_qualification framework="Training Needs">
      <component name="customer_level" max_score="2">
        <score value="0">Not assessed</score>
        <score value="1">Basic familiarity checked</score>
        <score value="2">Technical level and prior experience evaluated</score>
      </component>
      <component name="usage_goals" max_score="2">
        <score value="0">Not explored</score>
        <score value="1">General goals mentioned</score>
        <score value="2">Specific use cases and success metrics defined</score>
      </component>
      <component name="learning_preferences" max_score="2">
        <score value="0">Not asked</score>
        <score value="1">Preferred format mentioned</score>
        <score value="2">Learning style, pace, and preferred channels identified</score>
      </component>
      <component name="timeline_milestones" max_score="2">
        <score value="0">No timeline set</score>
        <score value="1">General timeframe mentioned</score>
        <score value="2">Specific milestones with dates agreed</score>
      </component>
      <calculation>(sum of applicable scores / (2 × number of applicable components)) × 100; null when no component applies</calculation>
    </needs_qualification>`;

    case 'complaint':
      return `
    <needs_qualification framework="Complaint Assessment">
      <component name="complaint_substance" max_score="2">
        <score value="0">Not clarified</score>
        <score value="1">General issue identified</score>
        <score value="2">Root cause and specific impact documented</score>
      </component>
      <component name="customer_expectation" max_score="2">
        <score value="0">Not explored</score>
        <score value="1">General expectation mentioned</score>
        <score value="2">Specific desired resolution confirmed</score>
      </component>
      <component name="previous_contacts" max_score="2">
        <score value="0">Not checked</score>
        <score value="1">Prior contact mentioned</score>
        <score value="2">Full history reviewed, previous promises identified</score>
      </component>
      <component name="urgency_risk" max_score="2">
        <score value="0">Not assessed</score>
        <score value="1">Frustration level noted</score>
        <score value="2">Churn/legal/reputation risk assessed and documented</score>
      </component>
      <calculation>(sum of applicable scores / (2 × number of applicable components)) × 100; null when no component applies</calculation>
    </needs_qualification>`;

    case 'cancellation':
      return `
    <needs_qualification framework="Churn Analysis">
      <component name="cancellation_reason" max_score="2">
        <score value="0">Not explored</score>
        <score value="1">General reason stated</score>
        <score value="2">Root cause identified with specific pain points</score>
      </component>
      <component name="usage_history" max_score="2">
        <score value="0">Not checked</score>
        <score value="1">Duration of use mentioned</score>
        <score value="2">Usage patterns and satisfaction history reviewed</score>
      </component>
      <component name="alternatives_considered" max_score="2">
        <score value="0">Not asked</score>
        <score value="1">Competitor mentioned</score>
        <score value="2">Specific alternatives and decision criteria explored</score>
      </component>
      <component name="retention_levers" max_score="2">
        <score value="0">Not explored</score>
        <score value="1">General offer made</score>
        <score value="2">Tailored retention offer based on customer's specific concerns</score>
      </component>
      <calculation>(sum of applicable scores / (2 × number of applicable components)) × 100; null when no component applies</calculation>
    </needs_qualification>`;

    case 'follow_up':
      return `
    <needs_qualification framework="Context Recall">
      <component name="previous_request" max_score="2">
        <score value="0">Not referenced</score>
        <score value="1">General reference to prior contact</score>
        <score value="2">Specific previous request and outcomes recalled</score>
      </component>
      <component name="status_update" max_score="2">
        <score value="0">Not provided</score>
        <score value="1">General status mentioned</score>
        <score value="2">Detailed progress with specific actions taken shared</score>
      </component>
      <component name="open_items" max_score="2">
        <score value="0">Not tracked</score>
        <score value="1">Some items mentioned</score>
        <score value="2">All open items listed with owners and deadlines</score>
      </component>
      <component name="next_steps" max_score="2">
        <score value="0">Not defined</score>
        <score value="1">Vague next steps mentioned</score>
        <score value="2">Concrete actions with timeline confirmed</score>
      </component>
      <calculation>(sum of applicable scores / (2 × number of applicable components)) × 100; null when no component applies</calculation>
    </needs_qualification>`;

    case 'other':
      return `
    <needs_qualification framework="General Inquiry">
      <component name="customer_request" max_score="2">
        <score value="0">Not clarified</score>
        <score value="1">General request understood</score>
        <score value="2">Specific request with details documented</score>
      </component>
      <component name="context" max_score="2">
        <score value="0">Not explored</score>
        <score value="1">Basic context gathered</score>
        <score value="2">Full background and history understood</score>
      </component>
      <component name="expected_outcome" max_score="2">
        <score value="0">Not asked</score>
        <score value="1">General expectation stated</score>
        <score value="2">Specific desired outcome and success criteria defined</score>
      </component>
      <component name="information_gathered" max_score="2">
        <score value="0">Minimal information</score>
        <score value="1">Basic details collected</score>
        <score value="2">Comprehensive information for resolution gathered</score>
      </component>
      <calculation>(sum of applicable scores / (2 × number of applicable components)) × 100; null when no component applies</calculation>
    </needs_qualification>`;
  }
}

// ============================================================================
// Objection Handling Rubric (universal)
// ============================================================================

/**
 * Get objection handling rubric — same for all categories
 */
function getObjectionHandlingRubric(): string {
  return `
    <objection_handling>
      <criteria>
        <criterion name="recognition" weight="1">Did agent acknowledge objection?</criterion>
        <criterion name="empathy" weight="1">Did agent validate concern?</criterion>
        <criterion name="clarification" weight="1">Did agent ask probing questions?</criterion>
        <criterion name="response" weight="2">Quality of solution/value reframe</criterion>
        <criterion name="confirmation" weight="1">Did agent verify resolution?</criterion>
      </criteria>
      <calculation>(sum / 6) × 100 per objection, then average</calculation>
    </objection_handling>`;
}

// ============================================================================
// Closing Rubric (10 categories × 2 channels)
// ============================================================================

/**
 * Shared across every category branch of `getClosingRubricBody`: a call that
 * never reached a point where closing was possible — stale/wrong contact,
 * customer disengaged during qualification, call disconnected within the
 * first ~30 seconds — is NOT the same as a call that reached a closing
 * moment and failed it. Each rubric's own score="0" band (no_close,
 * abandoned, hostile, no_outcome, ...) is reserved for the latter: a real,
 * scoreable failure. For the former, closing_techniques.score must be
 * `null`, not 0 — a 0 is a below-threshold data point a "weak closing"
 * alert can legitimately fire on; null says the metric is not applicable to
 * this call at all, and the alert engine skips null fields by design (see
 * `evaluateRuleSync` in business-engine.ts, the same mechanism already
 * relied on for `crm_completeness`).
 *
 * Appended once, in the wrapper below, so every category gets it — adding a
 * 10th category or renaming a score="0" technique can never forget it.
 *
 * Parametrized by channel (F2, 2026-08-12 Codex review): the note used to say
 * "this call" unconditionally. That was fine while chat only ever landed on
 * the 'purchase' category, but chat now routes through all 10 categories, so
 * the other nine chat rubrics carried a phone-specific "this call" note on a
 * conversation that was never a call.
 */
function getClosingNotApplicableNote(channel: Channel): string {
  const subject = channel === 'chat' ? 'this conversation' : 'this call';
  return `
    <closing_not_applicable_note>
      If the conversation ended before any closing attempt was possible, closing_techniques is
      not applicable to ${subject}: set score to null (not 0) and leave techniques_used empty.
    </closing_not_applicable_note>`;
}

/**
 * Get closing technique rubric — category + channel specific
 */
export function getClosingRubric(category: CallCategory, channel: Channel): string {
  return `${getClosingRubricBody(category, channel)}${getClosingNotApplicableNote(channel)}`;
}

/**
 * One closing technique, shared by both channels.
 *
 * The name and the score are the SAME object for call and chat on purpose:
 * they used to be hand-copied into two sibling branches per category, so
 * changing the score of `escalated_properly` or renaming `partial_resolution`
 * meant editing it twice, in nine places, with nothing tying the halves
 * together. Only the wording legitimately differs by channel.
 */
interface SharedClosingTechnique {
  name: string;
  score: number;
  /** Prose for the call rubric. */
  call: string;
  /** Prose for the chat rubric; omit when both channels word it the same. */
  chat?: string;
  /**
   * Chat-only name override. Exactly one technique needs it — `scheduling`,
   * where the same fallback is "I'll call back" on the phone and "I'll message
   * to confirm" in a chat. Anything else diverging by name is a sign the two
   * rubrics are drifting apart again.
   */
  chatName?: string;
}

/** What the rubric actually prints for one channel. */
interface RenderedTechnique {
  name: string;
  score: number;
  text: string;
}

/**
 * The nine categories whose call and chat rubrics grade the SAME techniques.
 * `Exclude<CallCategory, 'purchase'>` keeps it exhaustive: an 11th call
 * category fails the build here instead of silently losing its rubric.
 */
const SHARED_CLOSING_TECHNIQUES: Record<
  Exclude<CallCategory, 'purchase'>,
  readonly SharedClosingTechnique[]
> = {
  product_inquiry: [
    {
      name: 'demo_or_proposal_sent',
      score: 100,
      call: 'Demo scheduled or commercial proposal sent',
      chat: 'Demo link scheduled or proposal document sent in chat',
    },
    {
      name: 'followup_scheduled',
      score: 85,
      call: 'Follow-up call/meeting scheduled with date',
      chat: 'Follow-up message or call scheduled with a specific date',
    },
    {
      name: 'will_contact',
      score: 50,
      call: '"I\'ll send you the information" or "We\'ll contact you"',
      chat: '"I\'ll send you the information" or "We\'ll get back to you" with no date attached',
    },
    {
      name: 'no_close',
      score: 0,
      call: 'No next step proposed',
    },
  ],
  scheduling: [
    {
      name: 'meeting_confirmed',
      score: 100,
      call: 'Meeting date/time confirmed, invite sent',
      chat: 'Calendar link sent and slot confirmed in chat',
    },
    {
      name: 'date_agreed',
      score: 85,
      call: 'Date agreed, confirmation pending',
      chat: 'Date/time agreed, written confirmation still pending',
    },
    {
      name: 'will_call_back',
      chatName: 'will_message_back',
      score: 40,
      call: '"I\'ll call back to confirm"',
      chat: '"I\'ll message to confirm" — no date fixed',
    },
    {
      name: 'no_close',
      score: 0,
      call: 'No meeting scheduled',
    },
  ],
  support: [
    {
      name: 'resolved_confirmed',
      score: 100,
      call: 'Issue resolved, customer confirmed satisfaction',
      chat: 'Issue resolved, customer confirmed satisfaction in chat',
    },
    {
      name: 'resolved_followup',
      score: 85,
      call: 'Issue resolved, follow-up scheduled to verify',
      chat: 'Issue resolved, follow-up message scheduled to verify',
    },
    {
      name: 'escalated_properly',
      score: 70,
      call: 'Escalated to specialist with ticket number and timeline',
      chat: 'Escalated to specialist with a ticket number/link and timeline',
    },
    {
      name: 'partial_resolution',
      score: 50,
      call: 'Partial fix applied, remaining steps communicated',
      chat: 'Partial fix applied, remaining steps sent as written instructions',
    },
    {
      name: 'no_resolution',
      score: 20,
      call: 'Issue acknowledged but no solution provided',
    },
    {
      name: 'abandoned',
      score: 0,
      call: 'Customer left without resolution or next steps',
      chat: 'Customer stopped responding without resolution or next steps',
    },
  ],
  billing: [
    {
      name: 'resolved_confirmed',
      score: 100,
      call: 'Billing issue resolved, customer confirmed',
      chat: 'Billing issue resolved, customer confirmed in chat',
    },
    {
      name: 'resolved_followup',
      score: 85,
      call: 'Resolution applied, follow-up to verify',
      chat: 'Resolution applied, follow-up message sent to verify',
    },
    {
      name: 'escalated',
      score: 70,
      call: 'Escalated to billing team with timeline',
      chat: 'Escalated to billing team with a ticket number and timeline',
    },
    {
      name: 'partial_resolution',
      score: 50,
      call: 'Partial resolution, remaining items communicated',
      chat: 'Partial resolution, remaining items sent as a written summary',
    },
    {
      name: 'unresolved',
      score: 20,
      call: 'Issue acknowledged but not resolved',
    },
  ],
  onboarding: [
    {
      name: 'steps_assigned',
      score: 100,
      call: 'Clear action items assigned with deadlines',
      chat: 'Clear action items assigned with deadlines, sent as a checklist',
    },
    {
      name: 'materials_sent',
      score: 85,
      call: 'Training materials/access sent, next session scheduled',
      chat: 'Training materials/access links sent, next session scheduled',
    },
    {
      name: 'figure_out',
      score: 40,
      call: '"Try it out and let us know" — no structured follow-up',
    },
    {
      name: 'no_close',
      score: 0,
      call: 'No next steps or materials provided',
    },
  ],
  complaint: [
    {
      name: 'resolved_retained',
      score: 100,
      call: 'Complaint resolved, customer retained with satisfaction confirmed',
      chat: 'Complaint resolved, customer retained with satisfaction confirmed in chat',
    },
    {
      name: 'de_escalated',
      score: 85,
      call: 'Situation de-escalated, concrete resolution plan with timeline',
      chat: 'Situation de-escalated, concrete resolution plan with timeline sent',
    },
    {
      name: 'escalated_properly',
      score: 70,
      call: 'Escalated to manager/specialist with clear handoff',
      chat: 'Escalated to manager/specialist with a ticket number and clear handoff',
    },
    {
      name: 'partial_resolution',
      score: 50,
      call: 'Partial resolution, some concerns remain open',
    },
    {
      name: 'defensive',
      score: 20,
      call: 'Agent became defensive, no real resolution offered',
    },
    {
      name: 'hostile',
      score: 0,
      call: 'Situation worsened, customer more upset than at start',
    },
  ],
  cancellation: [
    {
      name: 'retained_new_terms',
      score: 100,
      call: 'Customer retained with new terms/offer accepted',
      chat: 'Customer retained with new terms/offer accepted in chat',
    },
    {
      name: 'retention_plan',
      score: 85,
      call: 'Retention plan proposed, customer considering',
      chat: 'Retention plan proposed in writing, customer considering',
    },
    {
      name: 'smooth_cancel',
      score: 60,
      call: 'Cancellation processed smoothly with positive exit experience',
    },
    {
      name: 'no_attempt',
      score: 20,
      call: 'No retention attempt, cancellation processed without engagement',
    },
  ],
  follow_up: [
    {
      name: 'concrete_next_steps',
      score: 100,
      call: 'Clear next steps with specific dates and owners agreed',
      chat: 'Clear next steps with specific dates and owners agreed in chat',
    },
    {
      name: 'will_contact',
      score: 50,
      call: '"I\'ll get back to you" — no specific date',
    },
    {
      name: 'no_close',
      score: 0,
      call: 'No next steps defined',
    },
  ],
  other: [
    {
      name: 'clear_outcome',
      score: 100,
      call: 'Conversation concluded with clear outcome and next steps',
    },
    {
      name: 'partial_outcome',
      score: 50,
      call: 'Some resolution but unclear next steps',
    },
    {
      name: 'no_outcome',
      score: 0,
      call: 'No clear conclusion or next steps',
      chat: 'No clear conclusion or next steps, customer stopped responding',
    },
  ],
};

/**
 * `purchase` is the one genuinely divergent pair: a chat closes by SENDING
 * something (calendar link, trial link), a call closes by ASKING. Different
 * techniques, different scores — sharing them would be a lie, so this pair
 * stays written out per channel.
 */
const PURCHASE_CLOSING_TECHNIQUES: Record<Channel, readonly RenderedTechnique[]> = {
  call: [
    {
      name: 'assumptive_close',
      score: 100,
      text: '"I\'ll get that set up for you. Best email for the contract?"',
    },
    {
      name: 'alternative_choice',
      score: 85,
      text: '"Would you prefer monthly or annual billing?"',
    },
    {
      name: 'direct_ask_value',
      score: 80,
      text: '"Based on the $10K/month savings, ready to move forward?"',
    },
    {
      name: 'trial_close',
      score: 60,
      text: '"How does that sound?"',
    },
    {
      name: 'weak_next_steps',
      score: 30,
      text: '"Let me know if you\'re interested"',
    },
    {
      name: 'no_close',
      score: 0,
      text: 'No closing attempt made',
    },
  ],
  chat: [
    {
      name: 'calendar_link',
      score: 100,
      text: 'Calendar link sent with context and confirmed',
    },
    {
      name: 'demo_trial_link',
      score: 90,
      text: 'Demo/trial link with clear CTA',
    },
    {
      name: 'next_step_specific',
      score: 80,
      text: 'Next step with specific date/time proposed',
    },
    {
      name: 'weak_ask',
      score: 40,
      text: '"Let me know if interested"',
    },
    {
      name: 'no_close',
      score: 0,
      text: 'No clear next step',
    },
  ],
};

const toRenderedTechnique = (
  technique: SharedClosingTechnique,
  channel: Channel
): RenderedTechnique =>
  channel === 'chat'
    ? {
        name: technique.chatName ?? technique.name,
        score: technique.score,
        text: technique.chat ?? technique.call,
      }
    : { name: technique.name, score: technique.score, text: technique.call };

function getClosingRubricBody(category: CallCategory, channel: Channel): string {
  const techniques: readonly RenderedTechnique[] =
    category === 'purchase'
      ? PURCHASE_CLOSING_TECHNIQUES[channel]
      : SHARED_CLOSING_TECHNIQUES[category].map((technique) =>
          toRenderedTechnique(technique, channel)
        );

  const rows = techniques
    .map(
      (technique) =>
        `      <technique name="${technique.name}" score="${technique.score}">${technique.text}</technique>`
    )
    .join('\n');

  return `
    <closing_technique category="${category}" channel="${channel}">
${rows}
    </closing_technique>`;
}

// ============================================================================
// Examples (by base category)
// ============================================================================

/**
 * Get category-specific examples (Phase 2.2)
 */
function getCategoryExamples(category: BaseCategory): string {
  switch (category) {
    case 'sales-call':
      return `  ${SALES_CALL_EXAMPLES}`;
    case 'support-call':
      return `  ${SUPPORT_CALL_EXAMPLES}`;
    case 'sales-chat':
      return `  ${SALES_CHAT_EXAMPLES}`;
    case 'complaint':
      return `  ${COMPLAINT_EXAMPLES}`;
  }
}
